import { useState, useEffect } from 'react';
import { Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  CalendarIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  CubeTransparentIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  NumberedListIcon,
  TruckIcon,
  UserGroupIcon,
  WrenchIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ArchiveBoxIcon, BoltIcon, BookOpenIcon, ChevronDownIcon, MagnifyingGlassIcon, WrenchScrewdriverIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import ACALogo from '../assets/acaStackLogo.png';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function Layout() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [userRole, setUserRole] = useState(null);

  // Fetch user profile data and role on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, email, role')
          .eq('id', user.id) // Now both IDs are UUIDs
          .single();
        if (error) throw error;
        if (data) {
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setEmail(data.email || user.email);
          setUserRole(data.role || '');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err.message);
        console.log('Failed to fetch user profile.');
      }
    };

    fetchUserProfile();
  }, [user]);

  // Function to update user profile
  const handleUpdateProfile = async () => {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: firstName,
          last_name: lastName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id); // Now both IDs are UUIDs
      if (error) throw error;
      setIsProfileModalOpen(false);
      console.log('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err.message);
      console.log('Failed to update profile.');
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: location.pathname === '/' },
    { name: 'Calendar', href: '/calendar', icon: CalendarIcon, current: location.pathname === '/calendar' },
    { name: 'Jobs', href: '/jobs', icon: FolderIcon, current: location.pathname === '/jobs' },
    { name: 'Kanban', href: '/kanban', icon: ArchiveBoxIcon, current: location.pathname === '/kanban' },
    { name: 'Parts', href: '/parts', icon: CubeTransparentIcon, current: location.pathname === '/parts' },
    { name: 'Production', href: '/production', icon: BoltIcon, current: location.pathname === '/production' },
    { name: 'Customers', href: '/customers', icon: UserGroupIcon, current: location.pathname === '/customers' },
    { name: 'Deliveries', href: '/deliveries', icon: TruckIcon, current: location.pathname === '/deliveries' },
    { name: 'Machines', href: '/machines', icon: WrenchIcon, current: location.pathname === '/machines' },
    { name: 'Quote', href: '/general-quotes', icon: NumberedListIcon, current: location.pathname === '/quote' },
    
    ...(userRole === 'admin' ? [
      { name: 'Financials', href: '/financials', icon: ChartPieIcon, current: location.pathname === '/financials' },
      { name: 'Invoices', href: '/invoices', icon: DocumentDuplicateIcon, current: location.pathname === '/invoices' },
    ] : []),
  ];

  const userNavigation = [
    { name: 'Your profile', href: '/settings' },
    { name: 'Sign out', onClick: signOut },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Machine Shop"
                src={ACALogo}
                className="h-11 object-cover rounded-md"
              />
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={classNames(
                              item.current
                                ? 'bg-gray-50 text-indigo-600'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                              'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                            )}
                          >
                            <item.icon
                              aria-hidden="true"
                              className={classNames(
                                item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                'size-6 shrink-0',
                              )}
                            />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto gap-2">
                    <div className="border-t border-gray-200 pt-4">
                      <button
                        onClick={() => setIsProfileModalOpen(true)}
                        className="w-full flex items-center gap-x-3 border-2 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                      >
                        <Cog6ToothIcon className="size-6 text-gray-400 group-hover:text-indigo-600" aria-hidden="true" />
                        My Profile
                      </button>
                      <button
                        onClick={signOut}
                        className="w-full flex items-center justify-center rounded-md p-2 mt-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-400"
                      >
                        Logout
                      </button>
                    </div>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop with collapsible functionality */}
      <div
        className={classNames(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col border-r border-gray-200 bg-white transition-all duration-300',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-52',
        )}
      >
        {/* Header area with a collapse/expand button */}
        <div className="flex h-16 shrink-0 items-center px-4">
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="mr-2 text-gray-600 hover:text-indigo-600"
          >
            {sidebarCollapsed ? (
              <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 rotate-90" aria-hidden="true" />
            )}
            <span className="sr-only">Toggle sidebar</span>
          </button>
          {!sidebarCollapsed && (
           <img
                alt="Machine Shop"
                src={ACALogo}
                className="h-11 object-cover rounded-md"
              />
          )}
        </div>

        {/* Nav Items */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto px-2 pb-4">
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex items-center rounded-md p-2 text-sm font-semibold',
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className={classNames(
                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'size-6 shrink-0',
                      )}
                    />
                    {!sidebarCollapsed && <span className="ml-3">{item.name}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          {/* Footer (Profile & Logout) */}
          <div className="mt-auto border-t border-gray-200 pt-4">
            <button
              onClick={() => setIsProfileModalOpen(true)}
              className={classNames(
                'flex items-center gap-x-3 w-full rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                sidebarCollapsed ? 'justify-center' : ''
              )}
            >
              <Cog6ToothIcon className="size-6 text-gray-400 group-hover:text-indigo-600" aria-hidden="true" />
              {!sidebarCollapsed && <span>My Profile</span>}
            </button>
            <button
              onClick={signOut}
              className={classNames(
                'mt-2 w-full flex items-center justify-center rounded-md p-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-400'
              )}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className={classNames('transition-all duration-300', sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-52')}>
        {/* Mobile Header with Menu Icon */}
        <div className="lg:hidden sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="flex-1 text-lg font-semibold text-gray-900">
            {navigation.find(item => item.current)?.name || 'Machine Shop'}
          </div>
        </div>

        <main className="min-h-screen py-4">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Profile Modal */}
      <Dialog open={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all duration-300 ease-in-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            <h2 className="text-xl font-semibold mb-4 text-gray-900">My Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  placeholder="Enter your last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  placeholder="Your email"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProfile}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
              >
                Save
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}

export default Layout;